"use client";

import React, { useState, useRef, useEffect } from "react";
import { ArrowRight, Gem } from "lucide-react";
import {
  motion,
  useInView,
  type Variants,
  useSpring,
  useTransform,
} from "framer-motion";
import Image from "next/image";
import Section, { SectionVariant } from "./section";
import { useIsMobile } from "@/hooks/use-mobile";
import type { HomepageSections, LocaleCode } from "@/types/sections";
import { pickLocalizedString } from "@/lib/locale";
import { resolveIcon } from "@/lib/icon-map";

type Props = {
  about: HomepageSections["about"];
  locale: LocaleCode;
};

/** Shared palette (same across your components) */
const PALETTE = [
  "#FCD2C3", // soft peach
  "#BBD8E6", // light blue
  "#AED8C7", // mint green
  "#F2D5A3", // warm beige
  "#D8D1C2", // stone grey
];

/** Rotate color every `durationSec` seconds; optional initial `delaySec` */
function useCyclingColor(durationSec: number, delaySec = 0) {
  const [idx, setIdx] = React.useState(0);

  // In the browser, setInterval/setTimeout return a number (DOM lib)
  const intervalRef = React.useRef<number | null>(null);
  const timeoutRef = React.useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const clearTimers = () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const tick = () => {
      if (!cancelled) {
        setIdx((i) => (i + 1) % PALETTE.length);
      }
    };

    // start after optional delay; kick once at phase start to match bounce
    const start = () => {
      tick();
      intervalRef.current = window.setInterval(tick, durationSec * 1000);
    };

    if (delaySec > 0) {
      timeoutRef.current = window.setTimeout(start, Math.max(0, delaySec * 1000));
    } else {
      start();
    }

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [durationSec, delaySec]);

  return PALETTE[idx];
}

/** Bynacore — Systems & Architecture “About/Services” section */
export default function AboutUsSection({ about, locale }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.1 });
  const isStatsInView = useInView(statsRef, { once: false, amount: 0.3 });
  const SectionIcon = resolveIcon(about.iconKey, Gem);
  const eyebrow = pickLocalizedString(about.eyebrow, locale);
  const title = pickLocalizedString(about.title, locale);
  const subtitle = pickLocalizedString(about.subtitle, locale);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.2 },
    },
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  type Service = {
    icon: React.ReactNode;
    secondaryIcon?: React.ReactNode;
    title: string;
    description: string;
    color: string;
    position: "left" | "right";
  };

  const services: Service[] = (about.services ?? []).map((service) => {
    const Icon = resolveIcon(service.iconKey, Gem);
    return {
      icon: <Icon className="w-6 h-6" style={{ color: service.color }} />,
      title: pickLocalizedString(service.title, locale),
      description: pickLocalizedString(service.description, locale),
      position: service.position,
      color: service.color || "#FCD2C3",
    };
  });

  const stats = (about.stats ?? []).map((stat) => {
    const Icon = resolveIcon(stat.iconKey, Gem);
    return {
      icon: <Icon className="w-6 h-6" />,
      value: stat.value,
      label: pickLocalizedString(stat.label, locale),
      suffix: stat.suffix ?? "",
    };
  });

  // === Dots color cycling, synced with bounce ===
  const DOT_BOUNCE_DURATION = 2; // keep in sync with your motion.transition.duration
  const dot1Color = useCyclingColor(DOT_BOUNCE_DURATION, 0);
  const dot2Color = useCyclingColor(DOT_BOUNCE_DURATION, 0.5); // phase shift to match delay
  const isMobile = useIsMobile()
  const [sectionVariant, setSectionVariant] = useState<SectionVariant>('center');
  React.useEffect(() => {
    // Transform state whenever breakpoint changes
    if (isMobile) {
      setSectionVariant('left');
    } else {
      setSectionVariant('center');
    }
  }, [isMobile])
  return (
    <Section
      id="about"
      eyebrow={eyebrow}
      eyebrowStyle={about.eyebrowStyle}
      title={title}
      titleStyle={about.titleStyle}
      subtitle={subtitle}
      subtitleStyle={about.subtitleStyle}
      Icon={SectionIcon}
      variant={sectionVariant}
    >
      <div
        id="about-section"
        ref={sectionRef}
        className="w-full py-5 bg-background text-primary overflow-hidden relative"
      >
        <motion.div
          className="container mx-auto relative z-10"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* === GRID BLOCK === */}
          {(() => {
            const left = services.filter((s) => s.position === "left");
            const right = services.filter((s) => s.position === "right");
            const rows = Math.max(left.length, right.length);

            return (
              <div className="relative grid grid-cols-1 gap-y-16 lg:grid-cols-[1fr_auto_1fr] lg:gap-x-12 lg:items-start">
                {/* CENTER (spans all rows on lg+) */}
                <div
                  className="flex justify-center items-start order-first lg:order-none mb-8 lg:mb-0 lg:col-start-2 my-10"
                  style={{ gridRow: `1 / span ${rows}` }}
                >
                  <motion.div
                    className="relative w-full max-w-[250px]"
                    variants={itemVariants}
                  >
                    <motion.div
                      className="rounded-md overflow-hidden"
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      whileHover={{
                        scale: 1.03,
                        transition: { duration: 0.3 },
                      }}
                    >
                      <Image
                        src="/logo.svg"
                        width={500}
                        height={500}
                        id="about-us-logo"
                        alt="Developers at work"
                        className="w-full h-full object-cover rounded-md"
                      />
                    </motion.div>

                    <motion.div
                      className="absolute inset-0 rounded-md -m-3 z-[-1]"
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                    />

                    {/* Top bouncing dot (color cycles every bounce) */}
                    <motion.div
                      className="absolute -top-10 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full"
                      style={{ backgroundColor: dot1Color }}
                      animate={{ y: [0, -10, 0], opacity: [0.5, 1, 0.5] }}
                      transition={{
                        duration: DOT_BOUNCE_DURATION,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                      }}
                    />

                    {/* Bottom bouncing dot (phase-shifted by 0.5s) */}
                    <motion.div
                      className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full"
                      style={{ backgroundColor: dot2Color }}
                      animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
                      transition={{
                        duration: DOT_BOUNCE_DURATION,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                        delay: 0.5,
                      }}
                    />
                  </motion.div>
                </div>

                {/* ROWS: pairs so left & right align per row */}
                {Array.from({ length: rows }).map((_, i) => (
                  <React.Fragment key={`row-${i}`}>
                    {/* LEFT cell (row i) */}
                    <div className="lg:col-start-1">
                      {left[i] && (
                        <ServiceItem
                          key={`left-${i}`}
                          icon={left[i].icon}
                          secondaryIcon={left[i].secondaryIcon}
                          title={left[i].title}
                          description={left[i].description}
                          color={left[i].color}
                          variants={itemVariants}
                          delay={i * 0.2}
                          direction="left"
                        />
                      )}
                    </div>

                    {/* RIGHT cell (row i) */}
                    <div className="lg:col-start-3">
                      {right[i] && (
                        <ServiceItem
                          key={`right-${i}`}
                          icon={right[i].icon}
                          secondaryIcon={right[i].secondaryIcon}
                          title={right[i].title}
                          description={right[i].description}
                          color={right[i].color}
                          variants={itemVariants}
                          delay={i * 0.2}
                          direction="right"
                        />
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            );
          })()}

          {/* Stats */}
          <motion.div
            ref={statsRef}
            className="mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            animate={isStatsInView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            {stats.map((stat, i) => (
              <StatCounter
                key={i}
                icon={stat.icon}
                value={stat.value}
                label={stat.label}
                suffix={stat.suffix}
                delay={i * 0.1}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </Section>
  );
}

/* ---------- Subcomponents ---------- */

interface ServiceItemProps {
  icon: React.ReactNode;
  secondaryIcon?: React.ReactNode;
  title: string;
  description: string;
  variants: Variants;
  delay: number;
  color: string;
  direction: "left" | "right";
}

function ServiceItem({
  icon,
  secondaryIcon,
  title,
  description,
  variants,
  delay,
  color = "#FCD2C3",
  direction,
}: ServiceItemProps) {
  return (
    <motion.div
      className="flex flex-col group"
      variants={variants}
      transition={{ delay }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <motion.div
        className="flex items-center gap-3 mb-3"
        initial={{ x: direction === "left" ? -20 : 20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: delay + 0.2 }}
      >
        <motion.div
          className="text-[#FCD2C3] bg-foreground/10 p-3 rounded-lg transition-colors duration-300 group-hover:bg-foreground/20 relative"
          // whileHover={{
          //   rotate: [0, -10, 10, -5, 0],
          //   transition: { duration: 0.5 },
          // }}
        >
          {icon}
          {secondaryIcon}
        </motion.div>
        <h1
          className={`text-2xl font-medium text-primary group-hover:text-[${color}] transition-colors duration-300`}
        >
          {title}
        </h1>
      </motion.div>

      <motion.p
        className="text-md text-primary/80 leading-relaxed pl-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: delay + 0.4 }}
      >
        {description}
      </motion.p>

      <motion.div
        className="mt-3 pl-12 flex items-center text-[#FCD2C3] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0 }}
      >
        <span className="flex items-center gap-1">
          Learn more <ArrowRight className="w-3 h-3" />
        </span>
      </motion.div>
    </motion.div>
  );
}

interface StatCounterProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  suffix: string;
  delay: number;
}

function StatCounter({ icon, value, label, suffix, delay }: StatCounterProps) {
  const countRef = useRef<HTMLDivElement>(null);
  const inView = useInView(countRef, { once: true });
  const [hasAnimated, setHasAnimated] = useState(false);

  const springValue = useSpring(0, { stiffness: 50, damping: 10 });
  useEffect(() => {
    if (inView && !hasAnimated) {
      springValue.set(value);
      setHasAnimated(true);
    } else if (!inView && hasAnimated) {
      springValue.set(0);
      setHasAnimated(false);
    }
  }, [inView, value, springValue, hasAnimated]);

  const displayValue = useTransform(springValue, (v) => Math.floor(v));

  return (
    <motion.div
      className="bg-foreground/90 backdrop-blur-sm p-6 rounded-xl flex flex-col items-center text-center group hover:bg-foreground dark:group-hover:bg-[#FCD2C3] transition-colors duration-300"
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
      }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <motion.div
        className="w-14 h-14 rounded-full bg-primary/5 dark:bg-black/20 flex items-center justify-center mb-4 text-[#FCD2C3] group-hover:bg-background/10 dark:group-hover:bg-background/80 transition-colors duration-300"
        // whileHover={{
        //   rotate: [0, -10, 10, -5, 0],
        //   transition: { duration: 0.8 },
        // }}
      >
        {icon}
      </motion.div>

      <motion.div
        ref={countRef}
        className="text-3xl font-bold text-secondary flex items-center"
      >
        <motion.span>{displayValue}</motion.span>
        <span>{suffix}</span>
      </motion.div>

      <p className="text-secondary/70 text-sm mt-1">{label}</p>
      <motion.div className="w-10 h-0.5 bg-[#FCD2C3] dark:bg-black mt-3 group-hover:w-16 transition-all duration-300" />
    </motion.div>
  );
}
